// Operation RIVERGHOST — Firebase client bootstrap.
//
// Single source of truth for SDK init, emulator wiring (localhost only),
// entry-code custom-token auth, App Check, FCM, and callable helpers.
// Every screen imports from here; nothing else initializes Firebase.

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  signInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as fbSignOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator,
  type Auth,
  type User,
} from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from "firebase/firestore";
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from "firebase/storage";
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
  type Functions,
} from "firebase/functions";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
} from "firebase/app-check";

// --- environment -----------------------------------------------------------

const IS_LOCAL =
  typeof location !== "undefined" &&
  (location.hostname === "localhost" || location.hostname === "127.0.0.1");

// Read Firebase web config from Vite env. In local/emulator mode a demo
// projectId is enough — the emulator ignores credentials.
const env = import.meta.env;
const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY ?? "demo-api-key",
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? "demo-riverghost.firebaseapp.com",
  projectId: env.VITE_FIREBASE_PROJECT_ID ?? "demo-riverghost",
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET ?? "demo-riverghost.appspot.com",
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "0",
  appId: env.VITE_FIREBASE_APP_ID ?? "demo-app-id",
};

export const VAPID_KEY: string | undefined = env.VITE_FCM_VAPID_KEY;
const RECAPTCHA_SITE_KEY: string | undefined = env.VITE_RECAPTCHA_SITE_KEY;

// --- init ------------------------------------------------------------------

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(app, "europe-west1");

if (IS_LOCAL) {
  // Point every SDK at the local Emulator Suite (ports from firebase.json).
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectStorageEmulator(storage, "localhost", 9199);
  connectFunctionsEmulator(functions, "localhost", 5001);
} else if (RECAPTCHA_SITE_KEY) {
  // App Check only in real (non-emulator) environments.
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

// Keep guests signed in across the many visits during the drip week.
void setPersistence(auth, browserLocalPersistence);

// --- auth helpers ----------------------------------------------------------

/** Redeem an entry code (via the redeemCode callable) and sign in. */
export async function signInWithEntryCode(
  code: string,
  deviceId: string,
): Promise<User> {
  const redeem = httpsCallable<
    { code: string; deviceId: string },
    { token: string }
  >(functions, "redeemCode");
  const { data } = await redeem({ code: code.trim(), deviceId });
  const cred = await signInWithCustomToken(auth, data.token);
  return cred.user;
}

export function onAuth(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, cb);
}

export function currentUser(): User | null {
  return auth.currentUser;
}

/** The single authorized admin Google account (the birthday host). */
export const ADMIN_EMAIL = "ouassim.fari@gmail.com";

/** Sign in the admin with Google (used only on the admin route). */
export async function signInWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider();
  const cred = await signInWithPopup(auth, provider);
  return cred.user;
}

/** True if the signed-in user is admin: the admin custom claim OR the admin email. */
export async function isAdmin(): Promise<boolean> {
  const u = auth.currentUser;
  if (!u) return false;
  const res = await u.getIdTokenResult();
  if (res.claims.role === "admin") return true;
  return u.email === ADMIN_EMAIL && u.emailVerified === true;
}

export function signOut(): Promise<void> {
  return fbSignOut(auth);
}

// --- callable helper -------------------------------------------------------

/** Typed wrapper around httpsCallable for the app's Cloud Functions. */
export function callable<Req, Res>(name: string) {
  const fn = httpsCallable<Req, Res>(functions, name);
  return async (payload: Req): Promise<Res> => (await fn(payload)).data;
}
